// Phone shell (< 1024 px): compact top bar · bottom tab bar (Today · Worklist · Files · Inbox · More) · sheets.
// The desktop sidebar layout in Layout.jsx is untouched — this only renders under the phone threshold.
import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sun, AlarmClock, FolderOpen, Inbox, Menu, Search, Globe, User, LogOut, Settings, Bug, Download, Share, X, Bell } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { Flag } from "@/components/Flag";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { DesktopOnlyNote } from "./DesktopOnlyNote";
import { MoreSheet, FilesSheet, InboxSheet } from "./PhoneSheets";
import { TOPBAR_OFFSET } from "./PhoneBits";
import { canInstall, onInstallAvailable, promptInstall, isIOS, isStandalone } from "@/lib/pwa";
import { permissionState, pushSupported } from "@/lib/push";

// One-time nudge on the installed app: from the second visit on, offer to switch notifications on.
function shouldOfferPush() {
  try {
    if (!isStandalone() || !pushSupported() || permissionState() !== "default" || localStorage.getItem("sf_push_prompted") === "1") return false;
    if (!sessionStorage.getItem("sf_visit_counted")) {
      sessionStorage.setItem("sf_visit_counted", "1");
      localStorage.setItem("sf_visits", String((parseInt(localStorage.getItem("sf_visits") || "0", 10) || 0) + 1));
    }
    return (parseInt(localStorage.getItem("sf_visits") || "0", 10) || 0) >= 2;
  } catch { return false; }
}

function PushHint({ onClose, onOpen }) {
  return (
    <div className="fixed inset-x-3 z-40 rounded-xl border border-slate-200 bg-slate-900 text-white shadow-xl px-3 py-2.5 flex items-start gap-2 sf-fade-up"
      style={{ bottom: "calc(4.5rem + env(safe-area-inset-bottom, 0px))" }} data-testid="phone-push-hint">
      <Bell className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
      <div className="text-[12px] leading-snug flex-1">
        <span className="font-semibold">Want a tap on the shoulder?</span> Get told when an email is assigned to you or an approval is waiting.
        <button type="button" onClick={onOpen} className="ml-1.5 underline font-semibold sf-tap" data-testid="phone-push-hint-open">Turn on</button>
      </div>
      <button onClick={onClose} className="h-7 w-7 -mr-1 -mt-0.5 inline-flex items-center justify-center rounded-full hover:bg-white/10 sf-tap" aria-label="Not now" data-testid="phone-push-hint-dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}

export const FILE_ROUTE = /^\/(aus\/)?collections\/ageing\/[^/]+$|^\/legal\/case\/[^/]+$/;

// Heavy tools keep their desktop layout inside a sideways-scrolling box and say so.
const DESKTOP_TOOLS = [
  { test: (p) => p.startsWith("/legal/timelines"), tool: "The timeline designer" },
  { test: (p) => p.startsWith("/legal/audit-matrix"), tool: "The audit matrix" },
  { test: (p) => p === "/portfolio", tool: "Portfolio analysis" },
  { test: (p) => p.startsWith("/sales-analytics") || p === "/sales-analysis" || p === "/funding-stats" || p === "/sales-stats", tool: "Sales planning & analytics" },
  { test: (p) => p.startsWith("/policy-centre"), tool: "The Policy Centre" },
  { test: (p) => p === "/applications" || p.startsWith("/applications/legacy") || p.startsWith("/workflow/") || p.startsWith("/applications/open-banking"), tool: "The underwriting workspace" },
  { test: (p) => p.startsWith("/settings") || p.startsWith("/admin/"), tool: "Portal Settings" },
  { test: (p) => p.startsWith("/legal/performance") || p.startsWith("/legal/credit-policy") || p.startsWith("/collections/analysis") || p === "/legal/handler-report", tool: "This analysis" },
];

function pageTitle(pathname, categories) {
  if (pathname === "/") return "Today";
  if (FILE_ROUTE.test(pathname)) return "Customer file";
  if (pathname === "/my-actions") return "My Actions";
  if (pathname === "/notifications") return "Notifications";
  if (pathname === "/profile") return "My Profile";
  if (pathname === "/audit-trail") return "Audit Trail";
  let best = null;
  for (const cat of categories) for (const it of cat.items) {
    if (pathname === it.path || pathname.startsWith(`${it.path}/`)) if (!best || it.path.length > best.path.length) best = it;
  }
  return best?.label || "SimplyFunded";
}

function IosHint({ onClose }) {
  return (
    <div className="fixed inset-x-3 z-40 rounded-xl border border-slate-200 bg-slate-900 text-white shadow-xl px-3 py-2.5 flex items-start gap-2 sf-fade-up"
      style={{ bottom: "calc(4.5rem + env(safe-area-inset-bottom, 0px))" }} data-testid="phone-ios-install-hint">
      <Share className="w-4 h-4 mt-0.5 shrink-0 text-sky-300" />
      <div className="text-[12px] leading-snug flex-1">
        <span className="font-semibold">Add SimplyFunded to your Home Screen</span> — tap <span className="font-semibold">Share</span> in Safari, then <span className="font-semibold">Add to Home Screen</span>. It opens full-screen like an app.
      </div>
      <button onClick={onClose} className="h-7 w-7 -mr-1 -mt-0.5 inline-flex items-center justify-center rounded-full hover:bg-white/10 sf-tap" aria-label="Dismiss" data-testid="phone-ios-install-dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}

export default function PhoneShell({ notifications, navigationCategories, bottomNav, onReportBug }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const { currentRegion, regionConfig, switchRegion, regions, allowedRegions } = useRegion();
  const [sheet, setSheet] = useState(null);           // files | search | inbox | more
  const [installable, setInstallable] = useState(canInstall());
  const [iosHint, setIosHint] = useState(() => { try { return isIOS() && !isStandalone() && localStorage.getItem("sf_ios_hint") !== "1"; } catch { return false; } });
  const [pushHint, setPushHint] = useState(shouldOfferPush);
  const dismissPushHint = () => { setPushHint(false); try { localStorage.setItem("sf_push_prompted", "1"); } catch { /* ignore */ } };

  useEffect(() => {
    document.body.classList.add("sf-phone");
    return () => document.body.classList.remove("sf-phone");
  }, []);
  useEffect(() => onInstallAvailable(setInstallable), []);
  useEffect(() => { setSheet(null); }, [location.pathname]);

  const pathname = location.pathname;
  const onFile = FILE_ROUTE.test(pathname);
  const tool = useMemo(() => DESKTOP_TOOLS.find((t) => t.test(pathname))?.tool || null, [pathname]);
  const title = pageTitle(pathname, navigationCategories);
  const ageingPath = currentRegion === "AUS" ? "/aus/collections/ageing" : "/collections/ageing";
  const inboxTotal = (notifications?.accountsInbox || 0) + (notifications?.legalInbox || 0) + (notifications?.appsInbox || 0) + (notifications?.legalMailInbox || 0);

  const tabs = [
    { key: "today", label: "Today", icon: Sun, to: "/", badge: notifications?.myActionsOverdue || 0, match: (p) => p === "/" || p === "/my-actions" || p === "/approvals" || p === "/credit-approvals" },
    hasPermission("can_view_collections") && { key: "worklist", label: "Worklist", icon: AlarmClock, to: ageingPath, match: (p) => /^\/(aus\/)?collections\/ageing$/.test(p) },
    { key: "files", label: "Files", icon: FolderOpen, sheet: "files", match: (p) => FILE_ROUTE.test(p) },
    { key: "inbox", label: "Inbox", icon: Inbox, sheet: "inbox", badge: inboxTotal, match: (p) => /inbox/.test(p) },
    { key: "more", label: "More", icon: Menu, sheet: "more" },
  ].filter(Boolean);

  const dismissIosHint = () => { setIosHint(false); try { localStorage.setItem("sf_ios_hint", "1"); } catch { /* ignore */ } };
  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-dvh bg-background" data-testid="phone-shell">
      <header className="fixed top-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-b sf-safe-top" data-testid="phone-topbar">
        <div className="h-12 flex items-center gap-1 pl-1 pr-1.5">
          <button type="button" onClick={() => navigate("/")} className="h-10 w-10 flex items-center justify-center rounded-lg sf-tap" aria-label="Today" data-testid="phone-home-mark">
            <img src="/icons/favicon-64.png" alt="" className="w-7 h-7" />
          </button>
          <div className="flex-1 min-w-0 font-semibold text-[15px] truncate" data-testid="phone-page-title">{title}</div>
          <button type="button" onClick={() => setSheet("search")} className="h-10 w-10 flex items-center justify-center rounded-lg text-slate-600 sf-tap active:bg-slate-100" aria-label="Search files" data-testid="phone-search-btn">
            <Search className="w-5 h-5" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="h-10 px-2 flex items-center gap-1 rounded-lg sf-tap active:bg-slate-100" aria-label="Switch region" data-testid="phone-region-switcher">
                <Flag code={regionConfig?.flagCode} size={20} />
                <Globe className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Switch Region</div>
              <DropdownMenuSeparator />
              {Object.entries(regions).map(([code, region]) => {
                const notGranted = !region.comingSoon && !allowedRegions.includes(code);
                const disabled = !!region.comingSoon || notGranted;
                return (
                  <DropdownMenuItem key={code} disabled={disabled} className={cn("flex items-center gap-2 h-10", currentRegion === code && "bg-blue-50")}
                    onClick={() => { if (disabled) return; switchRegion(code); navigate("/"); }} data-testid={`phone-region-option-${code}`}>
                    <Flag code={region.flagCode} size={22} />
                    <span className="flex-1">{region.name}</span>
                    {region.comingSoon ? <span className="text-[10px] uppercase text-amber-700">Soon</span>
                      : notGranted ? <span className="text-[10px] uppercase text-slate-500">No access</span>
                      : currentRegion === code && <Badge variant="secondary" className="text-xs">Active</Badge>}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="h-10 w-10 flex items-center justify-center rounded-full sf-tap active:bg-slate-100" aria-label="Account" data-testid="phone-user-menu">
                <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center"><User className="w-4 h-4" /></span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                <p className="text-[11px] text-muted-foreground">{user?.role || "Guest"}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="h-10" onClick={() => navigate("/profile")} data-testid="phone-menu-profile"><Settings className="w-4 h-4 mr-2" /> My Profile</DropdownMenuItem>
              {installable && <DropdownMenuItem className="h-10" onClick={() => promptInstall()} data-testid="phone-menu-install"><Download className="w-4 h-4 mr-2" /> Install app</DropdownMenuItem>}
              <DropdownMenuItem className="h-10" onClick={onReportBug} data-testid="phone-menu-bug"><Bug className="w-4 h-4 mr-2" /> Report a bug</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="h-10 text-red-600" onClick={handleLogout} data-testid="phone-menu-signout"><LogOut className="w-4 h-4 mr-2" /> Sign Out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main style={{ paddingTop: TOPBAR_OFFSET, paddingBottom: "calc(4rem + env(safe-area-inset-bottom, 0px))" }} data-testid="phone-main">
        {tool ? (
          <>
            <DesktopOnlyNote tool={tool} className="mx-3 mt-3" />
            <div className="overflow-x-auto" data-testid="phone-content"><div className="min-w-[1024px] p-4"><Outlet /></div></div>
          </>
        ) : (
          <div className={cn("min-w-0", onFile ? "" : "p-3")} data-testid="phone-content"><Outlet /></div>
        )}
      </main>

      {!onFile && (
        <nav className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t sf-safe-bottom" data-testid="phone-tabbar">
          <div className="grid h-16" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = t.match ? t.match(pathname) : false;
              return (
                <button key={t.key} type="button" onClick={() => (t.sheet ? setSheet(t.sheet) : navigate(t.to))}
                  className={cn("relative flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium sf-tap", active ? "text-primary" : "text-slate-500 active:text-slate-800")}
                  aria-current={active ? "page" : undefined} data-testid={`phone-tab-${t.key}`}>
                  <span className={cn("h-7 px-3 rounded-full flex items-center justify-center transition-colors", active && "bg-primary/10")}><Icon className="w-[22px] h-[22px]" /></span>
                  {t.label}
                  {t.badge > 0 && <span className="absolute top-1.5 left-1/2 ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`phone-tab-badge-${t.key}`}>{t.badge > 99 ? "99+" : t.badge}</span>}
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {iosHint && !onFile && <IosHint onClose={dismissIosHint} />}
      {pushHint && !iosHint && !onFile && pathname !== "/notifications" && <PushHint onClose={dismissPushHint} onOpen={() => { dismissPushHint(); navigate("/notifications"); }} />}

      <FilesSheet open={sheet === "files" || sheet === "search"} searchFirst={sheet === "search"} onClose={() => setSheet(null)} />
      <InboxSheet open={sheet === "inbox"} onClose={() => setSheet(null)} categories={navigationCategories} notifications={notifications} />
      <MoreSheet open={sheet === "more"} onClose={() => setSheet(null)} categories={navigationCategories} bottomNav={bottomNav} notifications={notifications}
        onReportBug={onReportBug} installable={installable} onInstall={promptInstall} user={user} onLogout={handleLogout} />
    </div>
  );
}
